import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageNavigation from '../components/PageNavigation.jsx';

export default function EchoChamberPage() {
  const navigate = useNavigate();
  const [isListening, setIsListening] = useState(false);
  const [activeVoice, setActiveVoice] = useState('present');
  const [displayText, setDisplayText] = useState('');
  const [echoResponse, setEchoResponse] = useState('');

  const voices = [
    { id: 'past', name: 'Past Voices', icon: '👴🏻', color: '#9333ea', desc: 'Preserved ancestral recordings' },
    { id: 'present', name: 'Present Reflections', icon: '🎤', color: '#6366f1', desc: 'Your current moments' },
    { id: 'future', name: 'Future Messages', icon: '👶🏻', color: '#a855f7', desc: 'Messages for tomorrow' }
  ];

  const sampleInteractions = [
    {
      input: "I miss Mom's voice",
      echo: "🎵 [Gently plays her laughter from 10 years ago...]\n\nShe'd say, 'Don't cry, love — I'm still here in every hug.'"
    },
    {
      input: "Tell me about Dad's favorite moment",
      echo: "🌅 [Showing photo from 1995...]\n\nHe always spoke of that sunrise at the manor. 'Some moments,' he'd say, 'are too beautiful for words — they belong to the heart.'"
    },
    {
      input: "I just made my first curry",
      echo: "👨‍🍳 [Dad's voice echoes softly...]\n\n'That's my child! Remember, it's not just about taste — it's about the love you stir in.'"
    }
  ];

  const handleSampleClick = (sample) => {
    setDisplayText(sample.input);
    setIsListening(true);
    
    setTimeout(() => {
      setEchoResponse(sample.echo);
      setIsListening(false);
    }, 2000);
  };

  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #000000 0%, #1a0033 50%, #000000 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Animated Background */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        opacity: 0.3,
        background: 'radial-gradient(circle at 20% 50%, rgba(147,51,234,0.15) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(99,102,241,0.15) 0%, transparent 50%)',
        animation: 'pulse 8s ease-in-out infinite'
      }} />

      <PageNavigation backTo="/category/legacy-hub" backLabel="← Back to Legacy Hub" />

      <div style={{ 
        maxWidth: 1200, 
        margin: '0 auto', 
        padding: '60px 20px',
        position: 'relative',
        zIndex: 1
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 60 }}>
          <div style={{ 
            fontSize: 96, 
            marginBottom: 24,
            animation: 'float 4s ease-in-out infinite',
            textShadow: '0 0 40px rgba(147,51,234,0.8), 0 0 80px rgba(147,51,234,0.5)'
          }}>
            🌌
          </div>
          <h1 style={{ 
            color: '#c9a62b', 
            fontSize: 56, 
            marginBottom: 20,
            fontFamily: 'Arsenica',
            textShadow: '0 2px 30px rgba(201,166,43,0.6)'
          }}>
            LIORA Echo Chamber
          </h1>
          <p style={{ 
            color: '#d1d5db', 
            fontSize: 20, 
            lineHeight: 1.8,
            maxWidth: 800,
            margin: '0 auto 24px'
          }}>
            Where voices across time converge. Speak to the past, record the present, and leave wisdom for the future.
          </p>
          <button
            onClick={() => navigate('/echo-chamber/info')}
            style={{
              background: 'rgba(147,51,234,0.2)',
              border: '1px solid rgba(147,51,234,0.4)',
              borderRadius: '9999px',
              padding: '8px 24px',
              color: '#c4b5fd',
              fontSize: 14,
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(147,51,234,0.3)';
              e.currentTarget.style.borderColor = 'rgba(147,51,234,0.6)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(147,51,234,0.2)';
              e.currentTarget.style.borderColor = 'rgba(147,51,234,0.4)';
            }}
          >
            ℹ️ Learn How It Works
          </button>
        </div>

        {/* Voice Mode Selector */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 24,
          marginBottom: 48,
          maxWidth: 1000,
          margin: '0 auto 48px'
        }}>
          {voices.map(voice => (
            <div
              key={voice.id}
              onClick={() => setActiveVoice(voice.id)}
              style={{
                background: activeVoice === voice.id 
                  ? `linear-gradient(135deg, ${voice.color}30, ${voice.color}10)`
                  : 'rgba(0,0,0,0.4)',
                border: `2px solid ${activeVoice === voice.id ? voice.color : 'rgba(255,255,255,0.1)'}`,
                borderRadius: 20,
                padding: 32,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                transform: activeVoice === voice.id ? 'scale(1.05)' : 'scale(1)',
                boxShadow: activeVoice === voice.id 
                  ? `0 12px 32px ${voice.color}40`
                  : 'none'
              }}
              onMouseEnter={(e) => {
                if (activeVoice !== voice.id) {
                  e.currentTarget.style.transform = 'scale(1.02)';
                  e.currentTarget.style.borderColor = voice.color;
                }
              }}
              onMouseLeave={(e) => {
                if (activeVoice !== voice.id) {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                }
              }}
            >
              <div style={{ fontSize: 48, marginBottom: 16, textAlign: 'center' }}>
                {voice.icon}
              </div>
              <h3 style={{ 
                color: voice.color, 
                fontSize: 22, 
                marginBottom: 12,
                fontFamily: 'Arsenica',
                textAlign: 'center'
              }}>
                {voice.name}
              </h3>
              <p style={{ 
                color: '#bdbdbd', 
                fontSize: 14, 
                lineHeight: 1.6,
                textAlign: 'center'
              }}>
                {voice.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Main Interaction Area */}
        <div style={{
          maxWidth: 900,
          margin: '0 auto 48px',
          background: 'linear-gradient(135deg, rgba(0,0,0,0.6) 0%, rgba(147,51,234,0.05) 100%)',
          border: '2px solid rgba(147,51,234,0.3)',
          borderRadius: 24,
          padding: 48,
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
        }}>
          {/* Voice Input Area */}
          <div style={{ marginBottom: 32 }}>
            <div style={{
              background: 'rgba(0,0,0,0.5)',
              borderRadius: 16,
              padding: 32,
              border: '1px solid rgba(147,51,234,0.2)',
              minHeight: 120,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 16
            }}>
              {isListening ? (
                <>
                  <div style={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #9333ea, #6366f1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    boxShadow: '0 0 30px rgba(147,51,234,0.8)'
                  }}>
                    <span style={{ fontSize: 40 }}>🎙️</span>
                  </div>
                  <p style={{ color: '#c4b5fd', fontSize: 18 }}>
                    Listening...
                  </p>
                </>
              ) : displayText ? (
                <p style={{ 
                  color: '#e5e7eb', 
                  fontSize: 20, 
                  fontStyle: 'italic',
                  textAlign: 'center'
                }}>
                  "{displayText}"
                </p>
              ) : (
                <>
                  <div style={{ fontSize: 48 }}>🎤</div>
                  <p style={{ color: '#9ca3af', fontSize: 16, textAlign: 'center' }}>
                    Click a sample below or speak your thoughts...
                  </p>
                </>
              )}
            </div>

            {/* Microphone Button */}
            <div style={{ textAlign: 'center', marginTop: 24 }}>
              <button
                onClick={() => setIsListening(!isListening)}
                style={{
                  width: 70,
                  height: 70,
                  borderRadius: '50%',
                  background: isListening 
                    ? 'linear-gradient(135deg, #ef4444, #dc2626)'
                    : 'linear-gradient(135deg, #9333ea, #7c3aed)',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 32,
                  boxShadow: isListening 
                    ? '0 8px 24px rgba(239,68,68,0.5)'
                    : '0 8px 24px rgba(147,51,234,0.5)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                {isListening ? '⏹️' : '🎙️'}
              </button>
            </div>
          </div>

          {/* Echo Response */}
          {echoResponse && (
            <div style={{
              background: 'linear-gradient(135deg, rgba(147,51,234,0.2) 0%, rgba(147,51,234,0.05) 100%)',
              borderLeft: '4px solid #9333ea',
              borderRadius: '0 16px 16px 0',
              padding: 32,
              marginTop: 32,
              animation: 'fadeIn 0.5s ease-in'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <span style={{ fontSize: 32 }}>🌌</span>
                <h4 style={{ 
                  color: '#c9a62b', 
                  fontSize: 20,
                  fontFamily: 'Arsenica',
                  margin: 0
                }}>
                  LIORA Response
                </h4>
              </div>
              <p style={{ 
                color: '#e5e7eb', 
                fontSize: 16, 
                lineHeight: 1.8,
                whiteSpace: 'pre-line',
                margin: 0
              }}>
                {echoResponse}
              </p>
            </div>
          )}
        </div>

        {/* Sample Interactions */}
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <h3 style={{ 
            color: '#c9a62b', 
            fontSize: 28, 
            marginBottom: 24,
            fontFamily: 'Arsenica',
            textAlign: 'center'
          }}>
            Try These Examples
          </h3>
          <div style={{ display: 'grid', gap: 20 }}>
            {sampleInteractions.map((sample, idx) => (
              <div
                key={idx}
                onClick={() => handleSampleClick(sample)}
                style={{
                  background: 'rgba(0,0,0,0.4)',
                  border: '1px solid rgba(147,51,234,0.3)',
                  borderRadius: 16,
                  padding: 24,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateX(8px)';
                  e.currentTarget.style.borderColor = 'rgba(147,51,234,0.6)';
                  e.currentTarget.style.background = 'rgba(147,51,234,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateX(0)';
                  e.currentTarget.style.borderColor = 'rgba(147,51,234,0.3)';
                  e.currentTarget.style.background = 'rgba(0,0,0,0.4)';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 24 }}>💬</span>
                  <p style={{ 
                    color: '#e5e7eb', 
                    fontSize: 16, 
                    fontStyle: 'italic',
                    margin: 0
                  }}>
                    "{sample.input}"
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Info Section */}
        <div style={{
          maxWidth: 900,
          margin: '60px auto 0',
          background: 'rgba(0,0,0,0.4)',
          border: '1px solid rgba(201,166,43,0.3)',
          borderRadius: 20,
          padding: 40,
          textAlign: 'center'
        }}>
          <h4 style={{ 
            color: '#c9a62b', 
            fontSize: 24, 
            marginBottom: 20,
            fontFamily: 'Arsenica'
          }}>
            How It Works
          </h4>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: 32,
            marginTop: 32
          }}>
            <div>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🎙️</div>
              <h5 style={{ color: '#9333ea', fontSize: 16, marginBottom: 8 }}>Voice Activated</h5>
              <p style={{ color: '#bdbdbd', fontSize: 13, lineHeight: 1.6 }}>
                Speak naturally or type your thoughts
              </p>
            </div>
            <div>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🤖</div>
              <h5 style={{ color: '#6366f1', fontSize: 16, marginBottom: 8 }}>AI Powered</h5>
              <p style={{ color: '#bdbdbd', fontSize: 13, lineHeight: 1.6 }}>
                Understands emotions and context
              </p>
            </div>
            <div>
              <div style={{ fontSize: 40, marginBottom: 12 }}>⏰</div>
              <h5 style={{ color: '#a855f7', fontSize: 16, marginBottom: 8 }}>Timeless</h5>
              <p style={{ color: '#bdbdbd', fontSize: 13, lineHeight: 1.6 }}>
                Connects past, present, and future
              </p>
            </div>
            <div>
              <div style={{ fontSize: 40, marginBottom: 12 }}>💝</div>
              <h5 style={{ color: '#c9a62b', fontSize: 16, marginBottom: 8 }}>Emotional</h5>
              <p style={{ color: '#bdbdbd', fontSize: 13, lineHeight: 1.6 }}>
                Responds with empathy and wisdom
              </p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
